/* WHICH SEVEN VIDEOS SIT AT THE TOP OF THE QUEUE.
 * ===========================================================================
 *
 * THIS FILE USED TO CARRY THE NAMES TOO, AND NO LONGER DOES.
 *
 * Until 21 Sep it translated "Machine_1" into "Servicing a brake caliper" when
 * rendering, deliberately, to avoid touching S3 — S3 has no rename, only
 * copy-then-delete, and `video.s3_path` is the sole link between a row and its
 * object. That was the right trade while the names were a presentation choice.
 *
 * It stopped being right once the names became the product. A display map and
 * the data disagreeing is its own defect: every future reader has to learn that
 * the file called Machine_1 is "really" a brake caliper, and every artifact,
 * every log line and every S3 listing keeps the serial number. So the objects,
 * the 204 artifacts and the database rows were all renamed — see
 * tools/rename_corpus.py — and `TITLES` is now empty because the file name IS
 * the readable name.
 *
 * WHAT IS LEFT HERE IS THE DEMO PIN, and only that. Six videos held at the
 * top of the queue for a client demo.
 *
 * ENDING THE DEMO
 * ---------------
 * Empty FEATURED. The pinning stops, the queue sorts as it always did, and no
 * other file needs editing. featured.test.ts asserts exactly that, so the
 * removal procedure is one that has been run rather than described.
 *
 * THE FAILURE MODE OF THIS ARRAY IS SILENCE. An entry that matches nothing does
 * not error — it just stops pinning. That is what happened during the rename,
 * when these still held the old file names, and it is why the entries below
 * carry their previous name in a comment.
 */

/** THE NAMES ARE IN S3 NOW, NOT HERE.
 *
 *  This map used to translate "Machine_1" into "Servicing a brake caliper" at
 *  render time. On 21 Sep the objects, the artifacts and the database rows were
 *  all renamed, so the file name IS the readable name and translating it would
 *  be translating it to itself.
 *
 *  Kept as an empty map rather than deleted, because `displayName` is the one
 *  place the app decides what to call a video and having that function exist —
 *  with nothing to say — is better than scattering `videoName` back through
 *  three screens. Add an entry only if a file name ever needs overriding
 *  WITHOUT renaming the object; think hard first, because that reintroduces the
 *  disagreement between screen and data this rename removed.
 *
 *  tools/rename_corpus.py parses this file, so the shape must stay. */
export const TITLES: Readonly<Record<string, string>> = {};

/** THE DEMO PIN — six videos held at the top of the queue, in this order.
 *
 *  SEPARATE FROM `TITLES` ON PURPOSE. Empty this array and the pinning stops
 *  while every name above survives, which is exactly the split you want when
 *  the demo is over. `featured.test.ts` asserts that.
 *
 *  Matched against the END of `video_path`, so it is independent of the bucket,
 *  the prefix and the environment — the same entry works on a laptop and on
 *  EC2, whose corpora are not the same size.
 *
 *  ORDER IS THE ORDER ON SCREEN. Index 0 sits at the very top.
 *
 *  REPLACED 23 Sep 2026. The earlier seven (Cooking an eggplant dish, Cooking
 *  pasta with sauce, Cleaning a bathroom, Cleaning a bathroom sink, Washing an
 *  outdoor patio, Trimming garden bushes, Playing with a dog) were the 21 Sep
 *  demo set. This is a different set for a later client demo. All six were
 *  verified against the real corpus (the rename map and real S3 paths in
 *  local adjudication artifacts) before this change; none are among the 21
 *  retired videos.
 *
 *  CHANGED 8 Oct 2026 (Utsav). Watering plants and Getting fruits & vegetables
 *  are out: both are among the 6 videos too hard for the AI and for the
 *  testers (claude/top6-sherlock-20261008.md; local copies moved to
 *  crowd_eval/videos/not_suitable_for_ai). In, in the same two slots: two
 *  outdoor, non-kitchen videos with high accuracy under the current judge
 *  (LLM-2 20261007b + rules 11/8, GT_20261005): Washing an outdoor patio
 *  ((a) 93.3%, 83% auto-approved, LLM-1 wrong 6%) and Herbicide spray
 *  ((a) 93.8%, 84%, 16%). The other four pins are unchanged. */
export const FEATURED: readonly string[] = [
  "Repairing a laptop",              // was Machine_4 before the 21 Sep rename
  "Fitting laminate flooring",       // was Making furniture_2
  "Washing an outdoor patio",        // 8 Oct: replaces Watering plants
  "Rebuilding a gearbox",            // was Repairing machine_2
  "Cleaning surface",
  "Herbicide spray",                 // 8 Oct: replaces Getting fruits & vegetables
];

/** The file name a path ends in, without its extension. Deliberately a private
 *  copy of `videoName`'s logic rather than an import: this module is meant to
 *  be deletable, and a circular import between it and format.ts would make
 *  removing it a refactor instead of a delete. */
function stem(path: string): string {
  return (path.split("/").pop() ?? path).replace(/\.[^.]+$/, "");
}

/** Where this video sits in the featured block, or Infinity if it is an
 *  ordinary one. Used as the FIRST sort key in the queue, so the seven stay
 *  together at the top whatever their review status is.
 *
 *  That does override the status ordering (2.3.5 — "work you are in the middle
 *  of first") for these seven, and only for these seven. It is the point: a
 *  video being half-reviewed must not move it off the top during a demo. */
export function featuredRank(path: string): number {
  const i = FEATURED.indexOf(stem(path));
  return i === -1 ? Number.POSITIVE_INFINITY : i;
}

/** True when this video is one of the seven — for anything that wants to mark
 *  them visually. */
export const isFeatured = (path: string): boolean =>
  Number.isFinite(featuredRank(path));

/** The title to SHOW. Falls back to the real file name, so every video that is
 *  not featured is completely unaffected. */
export function displayName(path: string): string {
  const s = stem(path);
  return TITLES[s] ?? s;
}
